// 환생 저장(dungeonhero.rebirth.v1, 레인 R — docs/REBIRTH-MODES-SPEC.md 2장): 영웅별 RebirthState + 환생 포인트 배분 + 모드별 진행(새 모드 소개 카드).
// 기존 저장(save.v1·profile.v2·growth.v2·gear.v1·stage.v1·skillslots.v1)은 형식을 바꾸지 않는다. 손상은 .bak/.latest에 남기고 .good으로 잇고,
// 미래 버전은 읽기만 하고 쓰지 않는다(game/gear.ts 방식). 연산은 id로 멱등(applied 목록 + 환생은 from 회차 검사).
//
// 원자성: 환생 한 번 = 이 키에 연산 하나를 쓰는 것. 레벨 1·트리 포인트 반환은 "회차별 성장 장부 키"(growthKeyOf)로 얻는다 —
// 새 회차 장부는 새 키라 처음엔 비어 있고(EXP 0·무료 랭크), 1회차 장부(growth.v2 / growth.lyra.v1)와 스테이지 진행(stage.v1)은 그대로 남는다
// (1회차 진행 보존: 환생 캐릭터도 1회차 마을·스테이지에 들어간다). 그래서 쓰기 하나가 성공하면 환생 전부가 성립하고, 실패하면 아무것도 바뀌지 않는다.
// 스킬 습득 상태는 kept(습득 스킬 비트)로 이어 간다(growthMods·learnedSkills의 kept 인자).
import type { HeroId, RebirthState, RpgMode } from '../shared/content/types';
import { learnedSkills } from '../shared/growth';
import { sumGear, weaponUsableBy, type GearTotals } from '../shared/gear';
import {
  EMPTY_ALLOC, MODE_NAME, cycleModeName, rebirthFallbackFb, STAR2_FB, REBIRTH_DENIAL_TEXT, REBIRTH_MAX_CYCLE, allocValid, makeRebirthStats, modeOfCycle, rebirthDenial,
  rebirthQuestDone, rebirthGateVisible, rebirthPointsAt, rebirthSlotBonus, rebirthStats, rebirthXpMult, rebornState,
  type RebirthAlloc, type RebirthCode, type RebirthCycleNo, type RebirthDenial, type RebirthLastView, type RebirthSaveMode, type RebirthStatsIn, type RebirthView,
} from '../shared/rebirth';
import { Scene, type GameCommand, type PlayableHeroId } from '../shared/types';
import { rewardId, type SaveStorage } from './save';
import { GrowthStore } from './growth';
import type { GameSim } from './game';

export const REBIRTH_KEY = 'dungeonhero.rebirth.v1';
/** 지금 빌드의 재육성 규칙 번호(C12 재육성 표) */
export const RELEVEL_RULE = 2;
/**
 * 이 시각(ms) 이후의 환생은 rr 칸이 없어도 새 규칙으로 본다 — 옛 빌드 탭이 rebirth.v1을 다시 쓰며 rr을 버려도 새 환생이 옛 규칙으로 돌아가지 않게.
 * 배포보다 넉넉히 뒤(2026-11-03 00:00 KST): 그 사이 환생은 rr이 있으면 새 규칙, 옛 빌드에서 환생해 rr이 없으면 옛 규칙(빠른 쪽 — 손해 없음)
 */
export const RELEVEL_RULE_SINCE_MS = Date.UTC(2026, 10, 2, 15, 0, 0);
/** 이 영웅의 지금 회차 재육성 규칙: 2 = 표, 1 = 옛 120마리(이 배포 전 환생). 회차 0은 해당 없음(2) */
export function relevelRuleOf(h: { readonly cycle: number; readonly rr?: number; readonly rebornAt: number | null }): number {
  if (h.cycle < 1) return RELEVEL_RULE;
  if (typeof h.rr === 'number' && h.rr >= RELEVEL_RULE) return RELEVEL_RULE;
  return h.rebornAt !== null && h.rebornAt >= RELEVEL_RULE_SINCE_MS ? RELEVEL_RULE : 1;
}
export const REBIRTH_VERSION = 1;
export const REBIRTH_BACKUP_KEY = REBIRTH_KEY + '.bak';
export const REBIRTH_LATEST_BACKUP_KEY = REBIRTH_BACKUP_KEY + '.latest';
export const REBIRTH_GOOD_KEY = REBIRTH_KEY + '.good';
const WRITE_ATTEMPTS = 5;
/** 저장에 남기는 적용 연산 id 수(최근 것만, 재전송·탭 합치기 멱등) */
export const APPLIED_CAP = 64;
const SEEN_LIMIT = 64;
const HERO_IDS: readonly HeroId[] = ['kael', 'lyra', 'seren', 'brann'];

/**
 * 회차별 성장 장부 키. 0회차 = 기존 키(카엘 growth.v2, 라이라 growth.lyra.v1 — GrowthStore 기본), 1회차부터 새 키.
 * 형식은 growth.v2와 같다(version 2 원장·결과 ID 멱등 영수증)
 */
export const REBIRTH_GROWTH_KEYS: Readonly<Record<PlayableHeroId, readonly [string, string]>> = {
  kael: ['dungeonhero.growth.kael.c1.v1', 'dungeonhero.growth.kael.c2.v1'],
  lyra: ['dungeonhero.growth.lyra.c1.v1', 'dungeonhero.growth.lyra.c2.v1'],
};
export function growthKeyOf(hero: PlayableHeroId, cycle: number): string | undefined {
  return cycle >= 1 ? REBIRTH_GROWTH_KEYS[hero][Math.min(2, cycle) - 1] : undefined;
}

/** 영웅 한 명의 환생 기록 */
export interface HeroRebirth extends RebirthState {
  /** 환생 포인트 배분(합 ≤ points, 재배분 무료) */
  readonly alloc: RebirthAlloc;
  /** 이어 가는 스킬 습득 비트(스킬 n = 비트 n−1). 환생할 때 그 회차에서 습득한 것을 더한다 */
  readonly kept: number;
  /** 소개 카드를 본 회차(모드별 진행: 새 모드 첫 화면) */
  readonly seen: readonly number[];
  /**
   * 이 회차 재육성 규칙(C12): 2 = 재육성 표(shared/field/relevel.ts), 없음(undefined) = 이 배포 전 환생(옛 '120마리 = 1레벨').
   * 이미 환생한 영웅이 배포 순간 Lv80대에서 수십 배 느려지지 않게 현재 회차 동안 옛 규칙을 유지하고 다음 환생부터 새 표(PR #328 리뷰 P1-b).
   */
  readonly rr?: number;
  /** 고정 대체 모드(C12 PR #330 리뷰 P2-1): 'arpg2' = 2차 환생을 ARPG ★2로 한 영웅(MMO 공개 뒤에도 ★2). 없으면 회차·공개 스위치로 계산 */
  readonly fb?: string;
}
export interface RebirthSave {
  version: number;
  rev: number;
  by: string;
  heroes: Partial<Record<HeroId, HeroRebirth>>;
  applied: string[];
}
export type RebirthOp =
  | { readonly id: string; readonly t: 'rebirth'; readonly hero: HeroId; readonly from: number; readonly at: number; readonly kept: number; readonly preview?: boolean }
  | { readonly id: string; readonly t: 'alloc'; readonly hero: HeroId; readonly alloc: RebirthAlloc }
  | { readonly id: string; readonly t: 'seen'; readonly hero: HeroId; readonly cycle: number };

export function freshHeroRebirth(hero: HeroId): HeroRebirth {
  return { heroId: hero, cycle: 0, mode: 'hackslash', points: 0, perks: [], rebornAt: null, alloc: EMPTY_ALLOC, kept: 0, seen: [] };
}
export function freshRebirthSave(): RebirthSave { return { version: REBIRTH_VERSION, rev: 0, by: '', heroes: {}, applied: [] }; }
/** 기록 없는 영웅의 공유 기본 기록(얼린 객체 — 프레임·틱마다 읽어도 새로 만들지 않는다. 쓰기 경로는 항상 복사본을 만든다) */
const DEFAULT_HERO: Readonly<Record<HeroId, HeroRebirth>> = (() => {
  const o = {} as Record<HeroId, HeroRebirth>;
  for (const h of HERO_IDS) {
    const f = freshHeroRebirth(h);
    o[h] = Object.freeze({ ...f, perks: Object.freeze([]) as readonly string[], alloc: Object.freeze({ ...f.alloc }), seen: Object.freeze([]) as readonly number[] });
  }
  return o;
})();
export function heroOf(s: RebirthSave, hero: HeroId): HeroRebirth { return s.heroes[hero] ?? DEFAULT_HERO[hero]; }
export function cloneSave(s: RebirthSave): RebirthSave {
  const heroes: Partial<Record<HeroId, HeroRebirth>> = {};
  for (const k of Object.keys(s.heroes) as HeroId[]) { const h = s.heroes[k]!; heroes[k] = { ...h, perks: [...h.perks], alloc: { ...h.alloc }, seen: [...h.seen] }; }
  return { version: s.version, rev: s.rev, by: s.by, heroes, applied: [...s.applied] };
}

const isInt = (v: unknown, min: number, max: number): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v >= min && v <= max;
const record = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);

/** 배분 합이 포인트를 넘으면 비례 축소(내림). 합이 이미 포인트 이하이면 그대로 */
function fitAlloc(atk: number, hp: number, crit: number, points: number): RebirthAlloc {
  const sum = atk + hp + crit;
  if (sum <= points) return { atk, hp, crit };
  const k = points / sum;
  return { atk: Math.floor(atk * k), hp: Math.floor(hp * k), crit: Math.floor(crit * k) };
}

type Parsed = { ok: true; save: RebirthSave } | { ok: false; reason: 'corrupt' | 'future' };
/** 원문 → 저장. 구조(JSON·타입·버전)가 깨진 것만 손상. 범위·파생 값은 영웅 단위로 보정한다 */
export function parseRebirth(raw: string): Parsed {
  let p: unknown;
  try { p = JSON.parse(raw); } catch { return { ok: false, reason: 'corrupt' }; }
  if (!record(p)) return { ok: false, reason: 'corrupt' };
  if (typeof p.version === 'number' && p.version > REBIRTH_VERSION) return { ok: false, reason: 'future' };
  if (p.version !== REBIRTH_VERSION || !isInt(p.rev, 0, Number.MAX_SAFE_INTEGER) || typeof p.by !== 'string' || !record(p.heroes) || !Array.isArray(p.applied)) return { ok: false, reason: 'corrupt' };
  if (!p.applied.every((x) => typeof x === 'string' && x.length > 0 && x.length <= 160)) return { ok: false, reason: 'corrupt' };
  const heroes: Partial<Record<HeroId, HeroRebirth>> = {};
  for (const [k, v] of Object.entries(p.heroes)) {
    if (!(HERO_IDS as readonly string[]).includes(k)) continue; // 모르는 영웅은 무시(다른 빌드)
    // 구조(타입)가 깨진 경우만 손상. 범위·파생 값(회차 상한·포인트 상한·모드·배분 합·kept 비트)은 영웅 단위로 보정한다 — 수치 가안을 바꿔도 저장이 잠기지 않게(형식 rebirth.v1 그대로)
    if (!record(v) || !isInt(v.cycle, 0, Number.MAX_SAFE_INTEGER) || !isInt(v.points, 0, Number.MAX_SAFE_INTEGER)) return { ok: false, reason: 'corrupt' };
    if (!(v.rebornAt === null || isInt(v.rebornAt, 0, Number.MAX_SAFE_INTEGER)) || !Array.isArray(v.perks) || !v.perks.every((x) => typeof x === 'string')) return { ok: false, reason: 'corrupt' };
    if (!isInt(v.kept, 0, Number.MAX_SAFE_INTEGER) || !Array.isArray(v.seen) || !v.seen.every((x) => isInt(x, 0, Number.MAX_SAFE_INTEGER))) return { ok: false, reason: 'corrupt' };
    const ra = v.alloc;
    if (!record(ra) || !isInt(ra.atk, 0, Number.MAX_SAFE_INTEGER) || !isInt(ra.hp, 0, Number.MAX_SAFE_INTEGER) || !isInt(ra.crit, 0, Number.MAX_SAFE_INTEGER)) return { ok: false, reason: 'corrupt' };
    const cycle = Math.min(REBIRTH_MAX_CYCLE, v.cycle) as RebirthCycleNo;
    const points = Math.min(v.points, rebirthPointsAt(cycle));
    heroes[k as HeroId] = { heroId: k as HeroId, cycle, mode: modeOfCycle(cycle, false, v.fb === STAR2_FB ? STAR2_FB : undefined), points, perks: v.perks as string[], rebornAt: v.rebornAt as number | null,
      alloc: fitAlloc(ra.atk, ra.hp, ra.crit, points), kept: (v.kept % 4096), seen: (v.seen as number[]).filter((x) => x <= REBIRTH_MAX_CYCLE),
      // 재육성 규칙(C12): 정수일 때만 이어 간다(모르는 값·없음 = 옛 규칙). 옛 빌드가 이 칸을 버려도 rebornAt 기준선이 새 환생을 가린다(relevelRuleOf)
      ...(isInt(v.rr, 1, 99) ? { rr: v.rr as number } : {}),
      // 고정 대체 모드: 회차 2이고 알려진 값일 때만(옛 빌드가 버리면 회차·공개 스위치 계산으로 돌아간다)
      ...(v.fb === STAR2_FB && cycle === 2 ? { fb: STAR2_FB } : {}) };
  }
  return { ok: true, save: { version: REBIRTH_VERSION, rev: p.rev, by: p.by, heroes, applied: p.applied as string[] } };
}
export function serializeRebirth(s: RebirthSave): string {
  return JSON.stringify({ version: REBIRTH_VERSION, rev: s.rev, by: s.by, heroes: s.heroes, applied: s.applied });
}

/** 연산 적용(제자리). 같은 id는 duplicate. 환생은 from 회차가 지금 회차와 같을 때만(다른 탭이 먼저 환생했으면 duplicate) */
export function applyRebirthOp(s: RebirthSave, op: RebirthOp): RebirthCode {
  if (s.applied.includes(op.id)) return 'duplicate';
  const h = heroOf(s, op.hero);
  let next: HeroRebirth;
  switch (op.t) {
    case 'rebirth': {
      if (h.cycle !== op.from) return h.cycle > op.from ? 'duplicate' : 'stale';
      if (h.cycle >= REBIRTH_MAX_CYCLE) return 'max_cycle';
      const r = rebornState(h, op.at);
      const fb = rebirthFallbackFb(r.cycle, !!op.preview);
      next = { ...h, ...r, mode: modeOfCycle(r.cycle, !!op.preview, fb), kept: h.kept | (op.kept & 4095), rr: RELEVEL_RULE, ...(fb ? { fb } : {}) };
      if (!fb && 'fb' in next) delete (next as { fb?: string }).fb;
      break;
    }
    case 'alloc':
      if (!allocValid(op.alloc, h.points)) return 'bad_alloc';
      next = { ...h, alloc: { atk: op.alloc.atk, hp: op.alloc.hp, crit: op.alloc.crit } };
      break;
    case 'seen':
      if (h.seen.includes(op.cycle)) return 'duplicate';
      next = { ...h, seen: [...h.seen, op.cycle] };
      break;
  }
  s.heroes[op.hero] = next;
  s.applied.push(op.id);
  if (s.applied.length > APPLIED_CAP) s.applied.splice(0, s.applied.length - APPLIED_CAP);
  return 'ok';
}

type ReadResult = { kind: 'none' } | { kind: 'ok'; save: RebirthSave } | { kind: 'corrupt'; raw: string } | { kind: 'future' } | { kind: 'unavailable' };

/** 저장 모듈: 마을 명령만 쓴다(고정 스텝 안에서 부르지만 프레임 루프가 아니라 명령 단위 — gear.command와 같다) */
export class RebirthStore {
  mode: RebirthSaveMode = 'ok';
  revision = 0;
  recovered = 0;
  private confirmed: RebirthSave = freshRebirthSave();
  private readonly nonce: string;
  private readonly seenReq = new Set<string>();

  constructor(private readonly storage: SaveStorage, options: { nonce?: string } = {}) {
    this.nonce = options.nonce ?? rewardId();
    this.refresh();
  }
  get state(): RebirthSave { return this.confirmed; }
  hero(h: HeroId): HeroRebirth { return heroOf(this.confirmed, h); }

  private read(): ReadResult {
    let raw: string | null;
    try { raw = this.storage.getItem(REBIRTH_KEY); } catch { return { kind: 'unavailable' }; }
    if (raw === null) return { kind: 'none' };
    const p = parseRebirth(raw);
    if (p.ok) return { kind: 'ok', save: p.save };
    return p.reason === 'future' ? { kind: 'future' } : { kind: 'corrupt', raw };
  }
  private readGood(): RebirthSave | null {
    try {
      const raw = this.storage.getItem(REBIRTH_GOOD_KEY);
      if (raw === null) return null;
      const p = parseRebirth(raw);
      return p.ok ? p.save : null;
    } catch { return null; }
  }
  private backupCorrupt(raw: string): boolean {
    try {
      const first = this.storage.getItem(REBIRTH_BACKUP_KEY);
      if (first === null) this.storage.setItem(REBIRTH_BACKUP_KEY, raw);
      else if (first !== raw && this.storage.getItem(REBIRTH_LATEST_BACKUP_KEY) !== raw) this.storage.setItem(REBIRTH_LATEST_BACKUP_KEY, raw);
      return this.storage.getItem(REBIRTH_BACKUP_KEY) === raw || this.storage.getItem(REBIRTH_LATEST_BACKUP_KEY) === raw;
    } catch { return false; }
  }
  private setMode(m: RebirthSaveMode): void { if (this.mode !== m) { this.mode = m; this.revision++; } }

  /** 저장소 최신으로 맞춘다(생성·명령 전). rev가 큰 쪽(같으면 저장소). 손상이면 백업 뒤 정상 사본으로 잇고 다음 쓰기에서 주 저장을 고친다 */
  refresh(): void {
    const r = this.read();
    switch (r.kind) {
      case 'unavailable': this.setMode('unavailable'); return;
      case 'future': this.setMode('future'); return;
      case 'none': this.setMode('ok'); break;
      case 'ok': if (r.save.rev >= this.confirmed.rev) this.confirmed = r.save; this.setMode('ok'); break;
      case 'corrupt': {
        if (!this.backupCorrupt(r.raw)) { this.setMode('unavailable'); return; }
        const good = this.readGood();
        if (!good) { this.setMode('corrupt'); return; }
        if (good.rev >= this.confirmed.rev) this.confirmed = good;
        this.recovered++;
        this.setMode('ok');
        // 주 저장을 정상 사본으로 되돌린다(실패해도 다음 명령의 쓰기가 다시 고친다)
        try { this.storage.setItem(REBIRTH_KEY, serializeRebirth(this.confirmed)); } catch { /* 다음 쓰기에서 */ }
        break;
      }
    }
    this.revision++;
  }

  /** 요청 id를 처음 봤으면 기억하고 true, 이미 본 것이면 false(거절 명령의 재전송도 한 번만 알린다) */
  markReq(requestId: string): boolean {
    if (this.seenReq.has(requestId)) return false;
    this.seenReq.add(requestId);
    if (this.seenReq.size > SEEN_LIMIT) { const o = this.seenReq.values().next(); if (!o.done) this.seenReq.delete(o.value); }
    return true;
  }

  /**
   * 연산 하나를 저장소 최신 위에 적용해 쓰고 되읽어 확인한다. requestId 재전송은 duplicate(아무것도 바꾸지 않는다).
   * 저장소 기준에서 이미 적용된 연산(다른 탭·재로드)도 duplicate. 쓰지 못하면 save_failed(상태 그대로)
   */
  command(requestId: string, op: RebirthOp): RebirthCode {
    if (!this.markReq(requestId)) return 'duplicate';
    this.refresh();
    if (this.mode !== 'ok') return 'readonly';
    for (let attempt = 0; attempt < WRITE_ATTEMPTS; attempt++) {
      const next = cloneSave(this.confirmed);
      const code = applyRebirthOp(next, op);
      if (code !== 'ok') return code;
      next.rev = this.confirmed.rev + 1; next.by = this.nonce;
      const json = serializeRebirth(next);
      try { this.storage.setItem(REBIRTH_KEY, json); } catch { return 'save_failed'; }
      try { this.storage.setItem(REBIRTH_GOOD_KEY, json); } catch { /* 사본 실패는 주 저장 성공을 바꾸지 않는다 */ }
      const back = this.read();
      if (back.kind === 'ok' && back.save.applied.includes(op.id)) { this.confirmed = back.save; this.revision++; return 'ok'; }
      // 되읽기가 다르다(다른 탭이 사이에 덮어씀): 그 위에 다시
      if (back.kind === 'ok') { this.confirmed = back.save; continue; }
      return 'save_failed';
    }
    return 'save_failed';
  }
}

type RebirthCmd = Extract<GameCommand, { type: 'rebirth' | 'rebirthAlloc' | 'rebirthIntro' }>;
type Mutable<T> = { -readonly [K in keyof T]: T[K] };

/**
 * GameSim 연결(뷰·명령·전투 보정). GameView.rebirth로 노출한다(지금 영웅 기준, 읽기 전용 getter — 할당 없음).
 * modes: `?modes=1`(main.ts가 정한다) — 미공개 모드 열기 + 퀘스트 없이 Lv99만으로 환생
 */
export class RebirthController {
  readonly store: RebirthStore;
  modes = false;
  /** 전투 보정(환생 포인트 배분). 배분·영웅·회차가 바뀔 때만 다시 계산한다 */
  readonly stats: RebirthStatsIn & ReturnType<typeof makeRebirthStats> = makeRebirthStats();
  readonly last: Mutable<RebirthLastView> = { seq: 0, kind: 'none', code: 'ok', requestId: '', message: '' };
  readonly view: RebirthView;
  private statsKey = '';

  constructor(private readonly g: GameSim, storage: SaveStorage, options: { nonce?: string } = {}) {
    this.store = new RebirthStore(storage, options);
    const self = this;
    this.view = {
      get mode() { return self.store.mode; },
      get revision() { return self.store.revision + self.last.seq * 1000; },
      get cycle() { return self.cur.cycle; },
      // 모드는 회차 + 공개 스위치 + 미리보기에서 계산(C12 ARPG ★2 대체 — 저장의 mode 칸은 참고만)
      get modeId() { return modeOfCycle(self.cur.cycle, self.modes, self.cur.fb); },
      get modeName() { return cycleModeName(self.cur.cycle, self.modes, self.cur.fb); },
      get points() { return self.cur.points; },
      get alloc() { return self.cur.alloc; },
      get free() { const h = self.cur; return h.points - h.alloc.atk - h.alloc.hp - h.alloc.crit; },
      get kept() { return self.cur.kept; },
      get rebornAt() { return self.cur.rebornAt; },
      get gateVisible() { return self.gateVisible(); },
      get denial() { return self.denial(); },
      get modes() { return self.modes; },
      get introPending() { const h = self.cur; return h.cycle >= 1 && !h.seen.includes(h.cycle); },
      get xpMult() { return rebirthXpMult(self.cur.cycle); },
      get slotBonus() { return rebirthSlotBonus(self.cur.cycle); },
      last: this.last,
    };
  }

  /** 지금 영웅의 기록 */
  get cur(): HeroRebirth { return this.store.hero(this.g.heroId); }
  cycleOf(h: PlayableHeroId): number { return this.store.hero(h).cycle; }
  get xpMult(): number { return rebirthXpMult(this.cur.cycle); }

  /** 영웅별 성장 장부(그 영웅의 지금 회차 키, 습득 스킬 kept 포함) */
  makeGrowth(storage: SaveStorage, hero: PlayableHeroId): GrowthStore {
    const h = this.store.hero(hero);
    const gs = new GrowthStore(storage, { hero, key: growthKeyOf(hero, h.cycle) });
    gs.kept = h.kept;
    return gs;
  }

  /** 윤회의 문 퀘스트 완료(판정 = 마지막 플레이 지역 헬 보스, REBIRTH_QUEST_NOW). 헬 진행 저장(stage.hell.v1)을 읽는다 */
  questDone(): boolean { return rebirthQuestDone(this.g.hell.cleared); }
  denial(): RebirthDenial | null {
    return rebirthDenial({ level: this.g.growth.level, cycle: this.cur.cycle, questDone: this.questDone(), modes: this.modes });
  }
  gateVisible(): boolean {
    return this.g.flow.scene === Scene.Village && rebirthGateVisible(this.g.growth.level, this.cur.cycle, this.modes, this.g.flow.town);
  }

  /** 배분 → 전투 보정. 바뀌었으면 true(GameSim이 능력치를 다시 계산한다) */
  syncStats(): boolean {
    const h = this.cur;
    const key = h.heroId + ':' + h.alloc.atk + ':' + h.alloc.hp + ':' + h.alloc.crit;
    if (key === this.statsKey) return false;
    this.statsKey = key;
    rebirthStats(this.stats, h.alloc);
    return true;
  }

  /**
   * 레벨 동기화(스펙 2.2): 환생 회차(1+)면 장착 장비 합계를 지금 레벨로 다시 낸다(부위마다 min(1, (레벨+10)/아이템 레벨)).
   * 1회차는 손대지 않는다. 마을·입장·레벨업 때만 부른다(장비 장부의 재사용 배열, 할당 없음)
   */
  syncGear(out: GearTotals): void {
    if (this.cur.cycle < 1) return;
    const eq = this.g.gear.equippedItems();
    const w = eq[0];
    if (!weaponUsableBy(w, this.g.heroId)) eq[0] = null; // 다른 영웅 전용 무기는 빠진다(gearTotalsOf와 같다)
    sumGear(out, eq, this.g.growth.level);
    eq[0] = w;
  }

  /** 명령: 환생·배분·소개 카드 확인 */
  command(cmd: RebirthCmd): void {
    const hero = this.g.heroId;
    const denied = this.g.village.rebirthDenial(cmd.type);
    let code: RebirthCode;
    if (denied) code = this.store.markReq(cmd.requestId) ? denied : 'duplicate';
    else if (cmd.type === 'rebirth') code = this.rebirth(hero, cmd.requestId);
    else if (cmd.type === 'rebirthAlloc') code = this.store.command(cmd.requestId, { id: 'alloc:' + cmd.requestId, t: 'alloc', hero, alloc: cmd.alloc });
    else code = this.store.command(cmd.requestId, { id: 'seen:' + cmd.requestId, t: 'seen', hero, cycle: this.cur.cycle });
    if (code === 'duplicate' && cmd.type !== 'rebirthIntro') return;
    const l = this.last;
    l.seq++; l.kind = cmd.type === 'rebirth' ? 'rebirth' : cmd.type === 'rebirthAlloc' ? 'alloc' : 'intro'; l.code = code; l.requestId = cmd.requestId;
    l.message = code === 'ok' ? (cmd.type === 'rebirth' ? cycleModeName(this.cur.cycle, this.modes, this.cur.fb) + ' 모드로 환생했어요. 레벨 1부터 다시 시작해요.' : cmd.type === 'rebirthAlloc' ? '환생 포인트를 배분했어요.' : '')
      : (REBIRTH_TEXT[code] ?? '');
    if (code === 'ok') { this.syncStats(); this.g.refreshStats(); }
  }

  /**
   * 환생 적용: 조건 다시 확인 → 판 EXP 기록(마을이라 보통 없음) → 연산 하나 쓰기(cycle+1·포인트+10·kept) → 새 회차 성장 장부로 바꾸기.
   * 쓰기가 실패하면 장부를 바꾸지 않는다(아무것도 변하지 않음)
   */
  private rebirth(hero: PlayableHeroId, requestId: string): RebirthCode {
    const d = this.denial();
    if (d) return this.store.markReq(requestId) ? d : 'duplicate';
    const growth = this.g.growth;
    growth.commitRun();
    if (growth.pendingXp > 0 || !growth.lastSaveOk) return this.store.markReq(requestId) ? 'save_failed' : 'duplicate';
    const from = this.cur.cycle;
    const kept = learnedSkills(growth.level, growth.ranks, hero, growth.kept);
    const code = this.store.command(requestId, { id: 'rebirth:' + requestId, t: 'rebirth', hero, from, at: this.g.wallClock(), kept, preview: this.modes });
    if (code !== 'ok') return code;
    this.g.swapGrowth(hero, this.makeGrowth(this.g.save.backingStorage, hero));
    return 'ok';
  }
}

export const REBIRTH_TEXT: Readonly<Partial<Record<RebirthCode, string>>> = {
  ...REBIRTH_DENIAL_TEXT,
  readonly: '이 기기에서는 환생 기록을 바꿀 수 없어요.',
  save_failed: '저장하지 못해 환생하지 않았어요. 다시 시도해 주세요.',
  bad_alloc: '가진 포인트보다 많이 배분할 수 없어요.',
  stale: '환생 기록이 바뀌었어요. 다시 확인해 주세요.',
  paused: '일시정지 중에는 할 수 없어요.',
  no_menu: '윤회의 문이나 교관 창에서만 할 수 있어요.',
};
